import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import type { ColDef, SingleDate } from '@helu/hds';
import type { ApiDataSource } from '@/core/domain/data_source.definition';
import type { SHARED_DIMENSIONS } from '@/core/domain/dimension.definition';
import type { DataSourceFilterParameter } from '@/features/reports/reports.type';
import type {
  DisplayType,
  DomainName,
  GqlAccountingBookingsFilterInput,
  GqlEvaluateDataSourcesInput,
} from '@/gql/generated-composables';
import type {
  CalculatedColumnType,
  NegativeNumberFormat,
  WidgetType,
} from './definitions/widget.definition';
import type { BorderMode, BorderWidth } from './utils/tableStyling.util';

export enum WidgetDisplayMode {
  Configuration = 'configuration',
  Preview = 'preview',
}

//#region Data

export type MetricData = {
  value: number;
  [key: string]: string | number;
};

export type MetricLookup = Record<
  UUID,
  { metricName: string; metricDisplayType: DisplayType; metricType: string }
>;

export type UnavailableMetric = {
  id: UUID;
  name: string;
};

export type ChartMetricWarningType = 'invalidCalculation' | 'invalidFilters';

export type UseChartMetricWarningsArgs = {
  selectedMetricIds: MaybeRefOrGetter<UUID[]>;
  selectedFilters: MaybeRefOrGetter<DataSourceFilterParameter[] | undefined>;
  metrics: MaybeRefOrGetter<ApiDataSource[]>;
};

export type UseChartMetricWarningsReturn = {
  hasInvalidMetrics: ComputedRef<boolean>;
  hasUnavailableMetrics: ComputedRef<boolean>;
  hiddenMetricIds: ComputedRef<Set<UUID>>;
  invalidMetricIds: ComputedRef<Set<UUID>>;
  invalidMetricNames: ComputedRef<string[]>;
  shouldDisableInspection: (metricIds: UUID[]) => boolean;
  unavailableMetricNames: ComputedRef<string[]>;
};

export type ExtendedEvaluateDataSourcesInput = {
  evaluateDataSourcesInput: GqlEvaluateDataSourcesInput;
  selectedDrilldowns: string[];
  availableMetrics: MetricLookup;
  drilldown?: {
    domain: string | null;
    referenceColumn: string;
  } | null;
};

export type AvailableDimensionAttribute = {
  name: string;
  key: string;
  valueKey: string;
  valueParentDimension: string;
  type?: string;
};

export type AvailableDimension = {
  name: string;
  attributes: AvailableDimensionAttribute[];
};

export type ChartWidgetPointClickData = {
  value: string | number;
  displayType: DisplayType;
  drillDownData: InspectionDrillDownData[];
  dynamicInspectionDrillDownData?: InspectionDrillDownData[];
};

export type ColumnMemberItem = {
  id: string;
  label: string;
  integrationName: string;
};

export enum ColumnMemberInitialStatus {
  Pending = 'pending',
  Loaded = 'loaded',
  Empty = 'empty',
  Failed = 'failed',
}

export type ColumnMemberState = {
  members: ColumnMemberItem[];
  currentPage: number;
  hasNextPage: boolean;
  isLoading: boolean;
  requestId: number;
  failureCount: number;
  initialStatus: ColumnMemberInitialStatus;
};

export type UseColumnMembersFilterOptions = {
  dataSources: ComputedRef<UUID[]>;
  columns: ComputedRef<string[]>;
  selectedFilters: Ref<DataSourceFilterParameter[]>;
};
//#endregion

//#region Inspection
export type InspectionModalProps = {
  inspectionData: WidgetInspectionData;
};
export type InspectionDrillDownData = {
  entryType: string; // dimension label
  entryValue: string; // dimension member
};

export type InspectionExtendedFilter = {
  dimensionName: string;
  memberFilters: {
    attributeName: string;
    attributeValue: string;
  }[];
};

export type InspectionFilters = {
  metrics: UUID[];
  filters: GqlAccountingBookingsFilterInput;
};

export type WidgetInspectionData = {
  value: string | number;
  scope: string;
  metrics: UUID[];
  inspectionFilters?: GqlAccountingBookingsFilterInput;
  valueFormat?: TableValueFormat;
};

export type WidgetInspectionDataItem = {
  [key: string]: string | number;
};
//#endregion

//#region Table Columns
export type TableData = {
  metricId: UUID;
  rowHierarchy: UUID[];
  rowId: UUID;
  rowLabel: string;
  isGroupingAllowed: boolean;
  [key: string]: number | string | string[] | boolean;
};

export type ColumnGroupItem = {
  key: string;
  label: string;
  domainName?: Nullable<DomainName>;
};

export type ColumnGroupSelectedPayload = {
  oldGroupKey: string;
  newGroupKey: string;
};

export type KeySelectedPayload = {
  oldKey: string;
  newKey: string;
};

export type CalculatedColumn = {
  colId: string;
  headerName: string;
  memberColumnIds: string[];
  type: CalculatedColumnType;
  formula: FormulaMember[];
};

export type ColumnSourceType =
  | typeof SHARED_DIMENSIONS.SCENARIO.MEMBERS.ACTUALS.LABEL
  | 'SCENARIO'
  | CalculatedColumnType;

export type ColumnSource = {
  sourceId?: UUID;
  sourceType: ColumnSourceType;
  description: string;
};

export interface TableColDef extends ColDef<TableData> {
  context?: {
    formula?: FormulaMember[];
    columnSource: ColumnSource;
    columnOrder: {
      group: string;
      value: string;
    };
  };
}

export type ColumnGroupColDef = {
  text: string;
  key: string;
  sourceLabel?: string;
};

export type ColumnGroupVisibilityColDef = ColumnGroupColDef & {
  visible: boolean;
};

//#endregion

//#region table calculations
export enum FormulaMemberType {
  CONSTANT = 'CONSTANT',
  INVALID = 'INVALID',
  OPERATOR = 'OPERATOR',
  VARIABLE = 'VARIABLE',
}

type BaseFormulaMember<T extends FormulaMemberType> = {
  displayValue: string;
  type: T;
};

export type FormulaVariable = BaseFormulaMember<FormulaMemberType.VARIABLE> & {
  id: UUID;
};

export type FormulaConstant = BaseFormulaMember<FormulaMemberType.CONSTANT> & {
  value: string;
};

export type FormulaOperator = BaseFormulaMember<FormulaMemberType.OPERATOR> & {
  value: string;
};

export type FormulaInvalid = BaseFormulaMember<FormulaMemberType.INVALID>;

export type FormulaMember =
  | FormulaVariable
  | FormulaConstant
  | FormulaOperator
  | FormulaInvalid;
//#endregion

//#region Chart
export type SeriesConfiguration = {
  id: UUID;
  parentKey: string;
  visible: boolean;
};
export type Series = {
  id: string | number;
  name: string;
  stackGroupName?: string;
  displayType?: DisplayType;
};

export type StackGroup = Record<string, Series>;
//#endregion

//#region Formatting and Styling
export type TableValueFormat = {
  decimalSeparator: string;
  currencySymbol: string;
  negativeNumberFormat: NegativeNumberFormat;
  thousandSeparator: string;
  precision: number;
};

export type FormattingState = {
  bold: boolean;
  italic: boolean;
  border: BorderMode;
  borderWidth: BorderWidth;
  textStyle: Nullable<string>;
  bgStyle: Nullable<string>;
  borderStyle: Nullable<string>;
};

export type RowStyles = {
  id: string;
  style: string;
};

export type ColorStyle = {
  text: string;
  border: string;
  bg: string;
};

//#endregion

export enum DisplaySidebarItemType {
  APPEARANCE = 'APPEARANCE',
  VALUES_IN_ROW = 'VALUES_IN_ROW',
  NUMBER_SETTINGS = 'NUMBER_SETTINGS',
  VISUALIZATION = 'VISUALIZATION',
  COLOR_THEME = 'COLOR_THEME',
  KPI_PARTS = 'KPI_PARTS',
}

export type StatefulWidgetType = Exclude<
  WidgetType,
  | 'EDITOR'
  | 'SEPARATOR'
  | 'AI_HERO'
  | 'RECENT_REPORTS'
  | 'FAVORITE_REPORTS'
  | 'RECENT_IMPORTS'
  | 'SCENARIO_PUBLISHES'
  | 'CONNECTION_HEALTH'
  | 'METRICS_GRAPH'
  | 'VISUALISATION'
>;

export enum WidgetPlaceholderType {
  NO_CONFIGURATION = 'NO_CONFIGURATION',
  NO_DATA = 'NO_DATA',
  TIMEOUT = 'TIMEOUT',
  UNEXPECTED_ERROR = 'UNEXPECTED_ERROR',
  DATA_LIMIT_EXCEEDED = 'DATA_LIMIT_EXCEEDED',
}

export enum ScenarioDateRangeType {
  CUSTOM_RANGE = 'CUSTOM_RANGE',
  THIS_MONTH = 'THIS_MONTH',
  THIS_QUARTER = 'THIS_QUARTER',
  THIS_CALENDAR_YEAR = 'THIS_CALENDAR_YEAR',
  PREVIOUS_MONTH = 'PREVIOUS_MONTH',
  PREVIOUS_QUARTER = 'PREVIOUS_QUARTER',
  PREVIOUS_CALENDAR_YEAR = 'PREVIOUS_CALENDAR_YEAR',
  NEXT_MONTH = 'NEXT_MONTH',
  NEXT_QUARTER = 'NEXT_QUARTER',
  NEXT_CALENDAR_YEAR = 'NEXT_CALENDAR_YEAR',
  LAST_3_MONTHS = 'LAST_3_MONTHS',
  LAST_6_MONTHS = 'LAST_6_MONTHS',
  NEXT_3_MONTHS = 'NEXT_3_MONTHS',
  NEXT_6_MONTHS = 'NEXT_6_MONTHS',
  FUTURE_MONTHS_THIS_YEAR = 'FUTURE_MONTHS_THIS_YEAR',
  PAST_MONTHS_THIS_YEAR = 'PAST_MONTHS_THIS_YEAR',
  YEAR_TO_DATE = 'YEAR_TO_DATE',
  QUARTER_TO_DATE = 'QUARTER_TO_DATE',
  MONTH_TO_DATE = 'MONTH_TO_DATE',
  YEAR_TO_GO = 'YEAR_TO_GO',
  QUARTER_TO_GO = 'QUARTER_TO_GO',
  MONTH_TO_GO = 'MONTH_TO_GO',
}

export type ScenarioDateRange =
  | {
      scenarioId: UUID;
      rangeType: Exclude<
        ScenarioDateRangeType,
        ScenarioDateRangeType.CUSTOM_RANGE
      >;
      customRangeDates?: never;
    }
  | {
      scenarioId: UUID;
      rangeType: ScenarioDateRangeType.CUSTOM_RANGE;
      customRangeDates: SingleDate[] | number[];
    };
